import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import CallsBadge from '../components/CallsBadge';
import ImportantNotesButton from '../components/ImportantNotesButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    FileText, Search, RefreshCw, User, Eye, Trash2, Star, History
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import HistoriqueModal from '../components/HistoriqueModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
    a_envoyer: { label: 'A envoyer', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    envoye: { label: 'Envoye', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    relance_1: { label: 'Relance 1', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    relance_2: { label: 'Relance 2', color: 'bg-orange-100 text-orange-700 border-orange-300' },
    relance_3: { label: 'Relance 3', color: 'bg-orange-200 text-orange-800 border-orange-300' },
    relance_whatsapp: { label: 'Relance WhatsApp', color: 'bg-orange-200 text-orange-800 border-orange-400' },
    decision_pas_prise: { label: 'Decision pas prise', color: 'bg-violet-100 text-violet-700 border-violet-200' },
    interesse: { label: 'Interesse', color: 'bg-teal-100 text-teal-700 border-teal-200' },
    accepte: { label: 'Accepte', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    refuse: { label: 'Refuse', color: 'bg-red-100 text-red-700 border-red-200' },
};

// Stats cards rendered above the search bar. Ordered to match the funnel
// (à envoyer → envoyé → relances → décision → final). Colors mirror what
// the rows themselves use, with orange highlighting the relance chain.
const STATS_CARDS = [
    { key: 'all', label: 'Total', accent: 'bg-[#1B365D]', text: 'text-white', subtle: 'border-[#1B365D]' },
    { key: 'a_envoyer', label: 'A envoyer', accent: 'bg-amber-100', text: 'text-amber-700', subtle: 'border-amber-200' },
    { key: 'envoye', label: 'Envoye', accent: 'bg-blue-100', text: 'text-blue-700', subtle: 'border-blue-200' },
    { key: 'relance_1', label: 'Relance 1', accent: 'bg-orange-100', text: 'text-orange-700', subtle: 'border-orange-200' },
    { key: 'relance_2', label: 'Relance 2', accent: 'bg-orange-100', text: 'text-orange-700', subtle: 'border-orange-300' },
    { key: 'relance_3', label: 'Relance 3', accent: 'bg-orange-200', text: 'text-orange-800', subtle: 'border-orange-300' },
    { key: 'relance_whatsapp', label: 'Relance WhatsApp', accent: 'bg-orange-200', text: 'text-orange-800', subtle: 'border-orange-400' },
    { key: 'decision_pas_prise', label: 'Decision pas prise', accent: 'bg-violet-100', text: 'text-violet-700', subtle: 'border-violet-200' },
    { key: 'interesse', label: 'Interesse', accent: 'bg-teal-100', text: 'text-teal-700', subtle: 'border-teal-200' },
    { key: 'accepte', label: 'Accepte', accent: 'bg-emerald-100', text: 'text-emerald-700', subtle: 'border-emerald-200' },
    { key: 'refuse', label: 'Refuse', accent: 'bg-red-100', text: 'text-red-700', subtle: 'border-red-200' },
];

export default function Devis() {
    const navigate = useNavigate();
    const { canDelete } = useCan('devis');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatut, setFilterStatut] = useState('all');
    const [historyTarget, setHistoryTarget] = useState(null);
    // Aggregated counts by statut_devis, derived from a parallel fetch that
    // ignores the statut filter so the totals stay accurate as the user
    // clicks between cards. The search input still scopes the counts so the
    // numbers stay consistent with what's visible in the list.
    const [statsCounts, setStatsCounts] = useState({});
    const [statsTotal, setStatsTotal] = useState(0);

    const fetchDevis = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (filterStatut !== 'all') params.append('statut', filterStatut);
            const res = await axios.get(`${API_URL}/api/devis?${params}`, { withCredentials: true });
            // Push refused devis to the bottom, preserving existing order within groups
            const sorted = [...res.data].sort((a, b) => {
                const aRef = a.statut_devis === 'refuse' ? 1 : 0;
                const bRef = b.statut_devis === 'refuse' ? 1 : 0;
                return aRef - bRef;
            });
            setItems(sorted);
        } catch {
            toast.error('Erreur lors du chargement des devis');
        } finally {
            setLoading(false);
        }
    }, [search, filterStatut]);

    // Fetch counts independently of the statut filter so the stats cards
    // reflect the WHOLE matching dataset (not just the current statut).
    const fetchStats = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            const res = await axios.get(`${API_URL}/api/devis?${params}`, { withCredentials: true });
            const map = {};
            for (const d of res.data) {
                const s = d.statut_devis || 'a_envoyer';
                map[s] = (map[s] || 0) + 1;
            }
            setStatsCounts(map);
            setStatsTotal(res.data.length);
        } catch {
            // Stats are decorative — failure must not block the page.
        }
    }, [search]);

    useEffect(() => { fetchDevis(); }, [fetchDevis]);
    useEffect(() => { fetchStats(); }, [fetchStats]);

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Supprimer ce devis ?')) return;
        try {
            await axios.delete(`${API_URL}/api/devis/${id}`, { withCredentials: true });
            toast.success('Devis supprime');
            fetchDevis();
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const getJoursDisplay = (item) => {
        if (item.jours_restants === null || item.jours_restants === undefined) return '-';
        if (item.jours_depasse) {
            return (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-red-100 text-red-600 border border-red-200">
                    {item.jours_restants}j depasse
                </span>
            );
        }
        const color = item.jours_restants <= 3 ? 'bg-red-100 text-red-600 border-red-200' :
                      item.jours_restants <= 7 ? 'bg-orange-100 text-orange-600 border-orange-200' :
                      'bg-green-100 text-green-600 border-green-200';
        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold ${color}`}>
                {item.jours_restants}j
            </span>
        );
    };

    const getModifiedDisplay = (item) => {
        if (!item.updated_at) return '-';
        try {
            return formatDistanceToNow(new Date(item.updated_at), { addSuffix: true, locale: fr });
        } catch { return '-'; }
    };

    return (
        <Layout>
            <div className="max-w-6xl mx-auto" data-testid="devis-page">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Devis
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Suivez vos dossiers clients et gerez les devis</p>
                </div>

                {/* Stats cards — clickable filters by statut */}
                <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-2" data-testid="devis-stats-cards">
                    {STATS_CARDS.map(card => {
                        const count = card.key === 'all' ? statsTotal : (statsCounts[card.key] || 0);
                        const pct = statsTotal > 0 && card.key !== 'all' ? Math.round((count / statsTotal) * 100) : null;
                        const isActive = filterStatut === card.key;
                        return (
                            <button
                                key={card.key}
                                type="button"
                                onClick={() => setFilterStatut(card.key)}
                                className={`group relative overflow-hidden rounded-lg border ${card.subtle} bg-white text-left px-2.5 py-2 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-[#1B365D]/30 ${isActive ? 'ring-2 ring-[#1B365D] shadow-md' : ''}`}
                                data-testid={`stats-card-${card.key}`}
                            >
                                <div className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${card.key === 'all' ? `${card.accent} ${card.text}` : `${card.accent} ${card.text}`}`}>
                                    {card.label}
                                </div>
                                <div className="mt-1.5 flex items-baseline gap-1.5">
                                    <span className="text-xl font-bold text-[#1B365D] tabular-nums">{count}</span>
                                    {pct !== null && (
                                        <span className="text-[10px] text-gray-400 font-medium">{pct}%</span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input placeholder="Rechercher un client..." value={search} onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-10 bg-gray-50 border-gray-200 text-sm" data-testid="search-devis" />
                        </div>
                        <Select value={filterStatut} onValueChange={setFilterStatut}>
                            <SelectTrigger className="w-44 h-10 text-sm" data-testid="filter-statut-devis">
                                <SelectValue placeholder="Tous les statuts" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                <SelectItem value="a_envoyer">A envoyer</SelectItem>
                                <SelectItem value="envoye">Envoye</SelectItem>
                                <SelectItem value="relance_1">Relance 1</SelectItem>
                                <SelectItem value="relance_2">Relance 2</SelectItem>
                                <SelectItem value="relance_3">Relance 3</SelectItem>
                                <SelectItem value="relance_whatsapp">Relance WhatsApp</SelectItem>
                                <SelectItem value="decision_pas_prise">Decision pas prise</SelectItem>
                                <SelectItem value="interesse">Interesse</SelectItem>
                                <SelectItem value="accepte">Accepte</SelectItem>
                                <SelectItem value="refuse">Refuse</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#1B365D]" />
                        <h2 className="text-base font-bold text-[#1B365D]">Liste des devis ( {items.length} )</h2>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                            <FileText className="h-10 w-10 mb-2 text-gray-300" />
                            <p className="text-sm font-medium">Aucun devis</p>
                            <p className="text-xs mt-1">Les devis sont crees depuis l'onglet Inventaire</p>
                        </div>
                    ) : (
                        <>
                        {/* Mobile Cards */}
                        <div className="block md:hidden space-y-2 p-2">
                            {items.map((item) => {
                                const sCfg = STATUT_CONFIG[item.statut_devis] || STATUT_CONFIG.a_envoyer;
                                return (
                                    <div
                                        key={item.id}
                                        className="rounded-lg p-3 border border-gray-200 bg-gray-50/50 space-y-2 cursor-pointer hover:bg-amber-50/40 transition-colors"
                                        onClick={() => navigate(`/devis/${item.id}`)}
                                        data-testid={`devis-card-${item.id}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="h-8 w-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                                                    <User className="h-4 w-4 text-[#1B365D]" />
                                                </div>
                                                <span className="font-semibold text-sm text-[#1B365D] truncate">{item.nom}</span>
                                            </div>
                                            <Badge className={`${sCfg.color} text-xs shrink-0 border`}>{sCfg.label}</Badge>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-gray-500">
                                            <span>{item.commercial_name || 'Non assigne'}</span>
                                            {getJoursDisplay(item)}
                                        </div>
                                        <div className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#1B365D]"
                                                onClick={(e) => { e.stopPropagation(); navigate(`/devis/${item.id}`); }} data-testid={`view-devis-${item.id}`}>
                                                <Eye className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-400"
                                                disabled={!canDelete}
                                                title={!canDelete ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                                onClick={(e) => { e.stopPropagation(); handleDelete(item.id, e); }} data-testid={`delete-devis-${item.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50/50">
                                        <TableHead className="font-medium text-gray-500">Client</TableHead>
                                        <TableHead className="font-medium text-gray-500">Commercial</TableHead>
                                        <TableHead className="font-medium text-gray-500">Appels</TableHead>
                                        <TableHead className="font-medium text-gray-500">Jours restants</TableHead>
                                        <TableHead className="font-medium text-gray-500">Statut</TableHead>
                                        <TableHead className="font-medium text-gray-500">Modifie</TableHead>
                                        <TableHead className="font-medium text-gray-500 text-right">Actes</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.map((item) => {
                                        const sCfg = STATUT_CONFIG[item.statut_devis] || STATUT_CONFIG.a_envoyer;
                                        return (
                                            <TableRow key={item.id} data-testid={`devis-row-${item.id}`}
                                                className="transition-colors hover:bg-amber-50/30 cursor-pointer"
                                                onClick={() => navigate(`/devis/${item.id}`)}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                                                            <User className="h-4 w-4 text-[#1B365D]" />
                                                        </div>
                                                        <span className="font-semibold text-sm text-[#1B365D]">{item.nom}</span>
                                                        <ImportantNotesButton
                                                            prospectId={item.prospect_id}
                                                            prospectName={item.nom}
                                                            variant="icon-only"
                                                        />
                                                        <Star className="w-4 h-4 text-blue-400 fill-blue-400 shrink-0" />
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                                        {item.commercial_name ? (
                                                            <>
                                                                <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                                                                {item.commercial_name}
                                                            </>
                                                        ) : (
                                                            <span className="text-gray-400">Non assigne</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell><CallsBadge item={item} /></TableCell>
                                                <TableCell>{getJoursDisplay(item)}</TableCell>
                                                <TableCell>
                                                    <Badge className={`${sCfg.color} text-xs border`}>{sCfg.label}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm text-gray-500">{getModifiedDisplay(item)}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-[#C9A962]"
                                                            onClick={(e) => { e.stopPropagation(); setHistoryTarget({ prospectId: item.prospect_id, nom: item.nom }); }}
                                                            data-testid={`history-devis-${item.id}`}
                                                            title="Historique">
                                                            <History className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-[#1B365D]"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/devis/${item.id}`); }}
                                                            data-testid={`view-devis-${item.id}`}>
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                                                            disabled={!canDelete}
                                                            title={!canDelete ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                                            onClick={(e) => handleDelete(item.id, e)} data-testid={`delete-devis-${item.id}`}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                        </>
                    )}
                </div>
            </div>
            <HistoriqueModal
                open={!!historyTarget}
                onOpenChange={(o) => !o && setHistoryTarget(null)}
                prospectId={historyTarget?.prospectId}
                title={historyTarget?.nom}
            />
        </Layout>
    );
}
